import type { UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import type { IProductSubscriptionRepository } from '@repositories/productSubscriptionRepository/IProductSubscriptionRepository';
import { ProductSubscriptionListModel } from '@repositories/productSubscriptionRepository/models/ProductSubscriptionListModel';
import type { ProductSubscriptionFilter } from '@repositories/productSubscriptionRepository/productSubscription.types';
import type { Page } from '@type/Page';

const psRepo = repository.get<IProductSubscriptionRepository>(KEYS.PRODUCT_SUBSCRIPTION_REPOSITORY);

/**
 * queryKey를 필터 필드별로 명시적으로 나열해 직렬화 안정성을 확보한다.
 * `{ ...filter }` spread 는 `includesBillingStat` 같은 플래그 유무를 놓쳐
 * 다른 호출이 같은 캐시를 덮어쓰는 문제가 있었다.
 */
const serializeFilter = (filter?: ProductSubscriptionFilter) =>
  [
    filter?.keyword ?? null,
    filter?.status ?? null,
    filter?.sort ?? null,
    filter?.nextExpiryDateFrom ?? null,
    filter?.nextExpiryDateTo ?? null,
    filter?.productVendorName ?? null,
    filter?.utilizationRateFrom ?? null,
    filter?.utilizationRateTo ?? null,
    filter?.annualPaymentEstimateFrom ?? null,
    filter?.annualPaymentEstimateTo ?? null,
    filter?.actionRequired ?? null,
    filter?.actionTypes ?? null,
    filter?.ownerId ?? null,
    filter?.includesBillingStat ?? false,
    filter?.size ?? null,
  ] as const;

export const productSubscriptionQueryKey = {
  all: ['productSubscription'] as const,
  lists: () => [...productSubscriptionQueryKey.all, 'list'] as const,
  list: (filter?: ProductSubscriptionFilter) =>
    [...productSubscriptionQueryKey.lists(), ...serializeFilter(filter)] as const,
  listInfinite: (filter?: ProductSubscriptionFilter) =>
    [...productSubscriptionQueryKey.lists(), 'infinite', ...serializeFilter(filter)] as const,
};

/**
 * GET /api/v1/product-subscriptions 무한 스크롤 조회.
 * MZP2-6022: 앱(상품) 단위 구독 집계.
 */
export const useGetProductSubscriptionListInfinite = (
  queries?: Omit<ProductSubscriptionFilter, 'page'>,
  options?: UseInfiniteQueryOptions<Page<ProductSubscriptionListModel>>,
) =>
  useInfiniteQuery({
    queryKey: productSubscriptionQueryKey.listInfinite(queries),
    queryFn: async ({ pageParam = 0 }) => {
      const result = await psRepo.getList({ ...queries, page: pageParam });
      return {
        ...result,
        content: result.content.map(dto => new ProductSubscriptionListModel(dto)),
        pageParam,
      };
    },
    keepPreviousData: true,
    ...options,
    getNextPageParam: lastPage => (lastPage.last ? undefined : lastPage.number + 1),
  });
